import {
  type ContentManagementRetailerConfiguration,
  type HeroBannerBannerType,
  type HeroBannerFormData,
  type SelectOptionTypeTimezones,
} from 'domains/merchandising/types/heroBanner.types'
import {
  bannerTypes,
  bannerDestinationTypes,
} from 'domains/merchandising/constants/heroBanner.constants'
import timezones from 'domains/merchandising/constants/timezones'
import {
  type StorefrontPlacementInput,
  type PlacementPlatformEnum,
} from 'domains/merchandising/api'

export const isImagebanner = (bannerType: HeroBannerBannerType): boolean =>
  bannerType === bannerTypes.image

export const isTextBanner = (bannerType: HeroBannerBannerType): boolean =>
  bannerType === bannerTypes.text

export const mapCtaFromUrl = (
  retailerConfiguration: ContentManagementRetailerConfiguration,
  cta: { data: { openInNewWindow: boolean; url: string; openInNewTab: boolean }; typeName: string }
) => {
  const { slug, pbiConfiguration } = retailerConfiguration || {}
  const { domainName } = pbiConfiguration || {}

  if (!cta.data.url.startsWith(domainName)) {
    return {
      type: bannerDestinationTypes.external,
      target: cta.data.url,
    }
  }

  const path = cta.data.url.replace(`${domainName}/store/${slug}/`, '')
  const [type, target] = path.split('/')

  if (type === 'collections') {
    return {
      type: bannerDestinationTypes.collection,
      target,
    }
  }
  console.error('Unhandled CTA type: ', path, cta.data.url)
  throw new Error(`Unhandled CTA type: ${type}`)
}

export const generateDestinationUrl = (
  type: string,
  target: string,
  retailerConfiguration?: ContentManagementRetailerConfiguration
) => {
  const { slug, pbiConfiguration } = retailerConfiguration || {}
  const { domainName } = pbiConfiguration || {}

  if (target && type === bannerDestinationTypes.external) {
    return target
  } else if (target && type === bannerDestinationTypes.department) {
    return `${domainName}/store/${slug}/departments/${target}`
  } else if (target && type === bannerDestinationTypes.collection) {
    try {
      const { slug: collectionSlug, legacy_path: legacyPath } = JSON.parse(target)
      return `${domainName}/store/${slug}/${
        collectionSlug ? `collections/${collectionSlug}` : legacyPath
      }`
    } catch {
      return `${domainName}/store/${slug}/collections/${target}`
    }
  } else if (target && type === bannerDestinationTypes.product) {
    return `${domainName}/store/products/${target}`
  }

  return null
}

// Since all form values are stored as strings, map them to what the api expects
export const mapFormDataToInput = (formData: HeroBannerFormData): StorefrontPlacementInput => ({
  ...formData,
  platform: (formData.platform as string).split(',') as PlacementPlatformEnum[],
  scheduling: {
    startDate: formData.scheduling.startDate,
    endDate: formData.scheduling.endDate || null,
    timezone: formData.scheduling.timezone,
  },
  images: {
    large: formData.images.large,
    small: formData.images.small || formData.images.large,
  },
  destination:
    formData.destination.type && formData.destination.target
      ? {
          // Strip displayValue
          type: formData.destination.type,
          target: formData.destination.target,
        }
      : null,
  banners: formData.banners.map(({ id, images, ...banner }) => ({
    ...banner,
    id: id || undefined,
    images: {
      ...images,
      small: images.small || images.large,
    },
  })),
})

// Format timezone data to match react-select options
// Return data sorted by offset
export const getTimezoneOptions = (data = timezones): SelectOptionTypeTimezones[] =>
  data
    .map(timezone => ({
      offset: timezone.offset,
      label: timezone.label,
      isDisabled: !timezone.utc?.[0], // disable any value that has no values
      value: timezone.utc?.[0] || '',
    }))
    .sort((a, b) => a.offset - b.offset)
